import { NgZone, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { Router, RouterOutlet } from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { FirebaseAppCheckService } from './core/services/firebase/app-check.service';
import { ThemeService } from './core/services/theme/theme.service';
import { CommonModule } from '@angular/common';
import { LoadingService } from './core/services/loading/loading.service';
import { Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { AuthService } from './core/auth/services/auth.service';
import { NotificationsComponent } from './shared/components/notifications/notification.component';
import { SkeletonLoaderComponent } from './core/components/skeleton-loader/skeleton-loader.component';
import { environment } from '@env/environment';
import { UserPreferenceService } from './core/services/user-preference/user-preference.service';
import { CryptoService } from './core/services/encryption/crypto.service';
import { VaultStateService } from './core/services/encryption/vault-state.service';
import { PinEntryComponent } from './shared/components/pin-entry/pin-entry.component';
import { VaultSetupComponent } from '@features/system/settings/security/vault/vault.component';
import { HealthCheckService } from './core/services/health-check/health-check.service';
import { ServerConnectivityBannerComponent } from './shared/components/server-connectivity-banner/server-connectivity-banner.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    NotificationsComponent,
    SkeletonLoaderComponent,
    PinEntryComponent,
    VaultSetupComponent,
    ServerConnectivityBannerComponent,
  ],
  providers: [ThemeService],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  animations: [
    trigger('fade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-in', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('200ms ease-out', style({ opacity: 0 }))]),
    ]),
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  private themeService = inject(ThemeService);
  private authService = inject(AuthService);
  private userPreferenceService = inject(UserPreferenceService);
  private loadingService = inject(LoadingService);
  private cryptoService = inject(CryptoService);
  private vaultStateService = inject(VaultStateService);
  private healthCheckService = inject(HealthCheckService);

  private router = inject(Router);
  private ngZone = inject(NgZone);
  private appCheckService = inject(FirebaseAppCheckService);

  isVaultLocked = false;
  showVaultSetup = false;

  isLoading = true;
  isAuthenticated: 'loading' | 'authenticated' | 'unauthenticated' = 'loading';

  private destroy$ = new Subject<void>();
  constructor() {
    this.themeService.initTheme();
  }

  ngOnInit() {
    this.appCheckService.initialize();
    this.healthCheckService.checkHealth();

    this.authService
      .checkAuthOnLoad()
      .pipe(takeUntil(this.destroy$))
      .subscribe();

    this.cryptoService.checkInitialLockStatus();
    this.cryptoService.locked$
      .pipe(takeUntil(this.destroy$))
      .subscribe((locked) => {
        this.isVaultLocked = locked;
      });

    this.cryptoService.showVaultSetup$
      .pipe(takeUntil(this.destroy$))
      .subscribe((show) => {
        // Never show vault setup for root org or doorway sessions
        this.showVaultSetup = show && !this.vaultStateService.isBypassVault;
      });

    this.authService.isAuthenticated$
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((isAuthenticated) => {
        if (isAuthenticated === true) {
          this.isAuthenticated = 'authenticated';
          this.initAppData();
        } else if (isAuthenticated === false) {
          this.isAuthenticated = 'unauthenticated';
        }
      });

    this.loadingService.isLoading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loadingStatus) => {
        this.isLoading = loadingStatus;
      });

    if (Capacitor.isNativePlatform()) {
      this.initializeAppLinks();
      this.checkLaunchUrl();
    }
  }

  private async checkLaunchUrl() {
    const launchUrl = await App.getLaunchUrl();
    if (launchUrl?.url) {
      console.log('App launched with URL (Cold Start):', launchUrl.url);
      this.handleDeepLink(launchUrl.url);
    }
  }

  private appUrlListenerHandle: PluginListenerHandle | null = null;

  private async initializeAppLinks() {
    this.appUrlListenerHandle = await App.addListener('appUrlOpen', (event: any) => {
      console.log('App opened via deep link (Warm Start):', event.url);
      this.handleDeepLink(event.url);
    });
  }

  private handleDeepLink(urlStr: string) {
    this.ngZone.run(() => {
      // If we opened an in-app browser for auth, close it now
      // This is crucial for OAuth flows where a browser was opened internally
      Browser.close();

      try {
        // Use the URL API to safely parse the incoming link
        const url = new URL(urlStr);

        // Construct the slug from pathname and search (query params)
        // This handles both https://medease.app/path and medease://path formats
        // e.g., https://medease.app/auth-callback?tempToken=xxx -> /auth-callback?tempToken=xxx
        const slug = url.pathname + url.search;

        if (slug && slug !== '/') {
          console.log('Deep link navigation trigger:', slug);
          this.router.navigateByUrl(slug);
        } else if (slug === '/') {
          // Default to dashboard if it's just the root domain
          this.router.navigateByUrl('/dashboard');
        }
      } catch (error) {
        console.error('Error parsing deep link URL:', error, urlStr);

        // Fallback parsing for specific formats like medease:// handled by OS if URL API fails
        const manualSlug = urlStr.split('.app').pop();
        if (manualSlug && manualSlug !== urlStr) {
          console.log('Falling back to manual slug extraction:', manualSlug);
          this.router.navigateByUrl(manualSlug);
        }
      }
    });
  }

  private initAppData() {
    this.userPreferenceService.initializeData();
    this.cryptoService.loadSecuritySettings();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.appUrlListenerHandle) {
      this.appUrlListenerHandle.remove();
    }
  }

  onVaultSetupClose() {
    this.cryptoService.closeVaultSetup();
  }
}
